import moment, { Moment } from "moment";
import { AssertNever } from "../consts/assertNever.const";
import { PatientStatusUtils } from "../consts/commonConsts";
import { DateRangeDefaultOptions } from "../consts/dateRangeDefaultOptions";
import { DateUtils } from "../consts/dateUtils";
import { PatientId } from "../messages/ids";
import { CreateDropdownFilter, DropdownEntity, DropdownFilter } from "../services/dropdownFilter";
import {
  AgencyPatientDocument,
  PatientDocumentService,
  PatientDocumentStatus,
} from "../services/patientDocumentService";

const tableSettings: NgTable.IParamValues<AgencyPatientDocument> = {
  count: 25,
  sorting: {
    "document.createdAt": "desc",
  },
};

interface Filters {
  freeText: "";
  dateRange: {
    dueDate: {
      startDate: Moment | null;
      endDate: Moment | null;
    };

    documentSubmissionDate: {
      startDate: Moment | null;
      endDate: Moment | null;
    };
  };
  dropdowns: DropdownEntity<AgencyPatientDocument>[];
}

import {
  declareAiCapability,
  provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";
import { declareAiTabDestination } from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-tab-destinations";

// The tab saying it is somewhere to go. Care & Task management opens on Tasks, so this tab is only
// reachable by name if it says so itself.
declareAiTabDestination({
  id: "patientDocuments.open",
  title: "Open the Patients Documents tab",
  stateName: "app.patients.careAndTaskManagement.patientsDocuments",
  opens: "page:patientDocuments",
  description:
    "Opens Patients > Care & Task management > Patients Documents: the documents due from the agency's patients, when they are due and whether they came back.",
  location: "the tab bar of Care & Task management",
  permissionKey: "view_patients_section_upcoming_tasks",
  examples: ["open patient documents", "patient documents due this month"],
});

// The patient documents tab describing its own filter bar. Its two ranges mean different things - when
// the document is due, and when it was submitted - so they are named apart rather than folded into one.
declareAiCapability({
  id: "patientDocuments.filter",
  title: "Filter the patient documents list",
  effect: "prefill",
  description:
    "Sets the filters on Patients > Care & Task management > Patients Documents and re-applies them: the due-date range, the submission-date range, the patient's status, the document type, the document's status, the assigned nurse, and a free-text search over patient name or ID. Anything left out keeps what the page already has. Only works while that tab is open.",
  location: "the filter bar of the Patients Documents tab",
  scope: "page:patientDocuments",
  permissionKey: "view_patients_section_upcoming_tasks",
  params: [
    { name: "dueFrom", type: "date", required: false, description: "Earliest due date to include, as yyyy-MM-dd." },
    { name: "dueTo", type: "date", required: false, description: "Latest due date to include, as yyyy-MM-dd." },
    {
      name: "submittedFrom",
      type: "date",
      required: false,
      description: "Earliest submission date to include, as yyyy-MM-dd.",
    },
    {
      name: "submittedTo",
      type: "date",
      required: false,
      description: "Latest submission date to include, as yyyy-MM-dd.",
    },
    {
      name: "patientStatus",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getPatientDocumentOptions("Patient status"),
      description: "Limit to patients in these statuses.",
    },
    {
      name: "documentType",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getPatientDocumentOptions("Document type"),
      description: "Limit to these kinds of document.",
    },
    {
      name: "documentStatus",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getPatientDocumentOptions("Document status"),
      description: "The document's own state, e.g. missing, in progress, approved.",
    },
    {
      name: "assignedNurse",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getPatientDocumentOptions("Assigned nurse"),
      description: "Limit to documents assigned to these nurses.",
    },
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text the page matches against patient name or ID.",
    },
  ],
  examples: [
    "patient documents due this month",
    "missing documents for active patients",
    "documents assigned to nurse smith",
  ],
});

/**
 * The options one of this tab's dropdowns currently has, in the shape the omnibar reads.
 *
 * Built from the rows on screen rather than a fixed list, so they are read when the manifest is built
 * and are empty until the fetch lands.
 */
let patientDocumentDropdowns: () => { title: string; options: { id: unknown; label?: unknown }[] }[] =
  () => [];

function getPatientDocumentOptions(title: string) {
  const dropdown = patientDocumentDropdowns().find((one) => one.title === title);

  return (dropdown?.options ?? []).map((option) => ({
    value: String(option.id),
    label: String(option.label ?? option.id),
  }));
}

//! @ngInject
export class PatientsDocumentsCtrl {
  private documents: AgencyPatientDocument[] = [];
  private dropdownFilterManager: DropdownFilter<AgencyPatientDocument>;
  private dateRangeOptions: DateRangeDefaultOptions;

  table: NgTableParams<AgencyPatientDocument>;

  isLoading = false;

  filters: Filters = {
    freeText: "",
    dateRange: {
      dueDate: {
        startDate: null,
        endDate: null,
      },

      documentSubmissionDate: {
        startDate: null,
        endDate: null,
      },
    },
    dropdowns: [],
  };

  dropdownFilterExtraSettings = {
    styleActive: true,
    scrollable: true,
    enableSearch: true,
  };

  dropdownFilterEvents = {};
  documentStatusesForStats: Partial<
    Record<PatientDocumentStatus, { label: string; count: number; colorClass: string }>
  > = {
    OPEN_FOR_RESUBMISSION: { label: "Open for resubmission", count: 0, colorClass: "orange" },
    IN_PROGRESS: { label: "In progress", count: 0, colorClass: "grey" },
    SENT: { label: "Sent", count: 0, colorClass: "blue" },
    COMPLETED: { label: "Completed", count: 0, colorClass: "green" },
    SIGNED: { label: "Signed", count: 0, colorClass: "green" },
    MISSING: { label: "Missing", count: 0, colorClass: "red" },
    APPROVED: { label: "Approved", count: 0, colorClass: "green" },
    SENT_TO_PHYSICIAN: { label: "Sent to physician", count: 0, colorClass: "blue" },
    CHANGE_REQUESTED: { label: "Change requested", count: 0, colorClass: "orange" },
  };
  constructor(
    private patientDocumentService: PatientDocumentService,
    private NgTableParams: NgTable.ITableParamsConstructor<AgencyPatientDocument>,
    private toaster: toaster.IToasterService,
    private $rootScope: angular.IRootScopeService,
    private dateUtils: DateUtils,
    private dateRangeDefaultOptions: DateRangeDefaultOptions,
    private assertNever: AssertNever,
    private createDropdownFilter: CreateDropdownFilter,
    private patientStatusUtils: PatientStatusUtils,
    private $scope: angular.IScope
  ) {
    this.table = new this.NgTableParams(tableSettings, { dataset: [] });

    patientDocumentDropdowns = () => this.filters?.dropdowns ?? [];

    const releasePatientDocumentsFilter = provideAiCapability("patientDocuments.filter", (args) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      const asMoment = (text: unknown) => {
        const value = moment(String(text), "YYYY-MM-DD", true);

        if (!value.isValid()) {
          throw new Error(`Could not read the date ${String(text)}.`);
        }

        return value;
      };
      const setDropdown = (title: string, requested: unknown, what: string) => {
        const dropdown = this.filters.dropdowns.find((one) => one.title === title);

        if (dropdown === undefined) {
          throw new Error(`This page has no ${title} filter.`);
        }

        const wanted = asList(requested).map((one) => String(one).toLowerCase());
        const chosen = dropdown.options.filter(
          (option) =>
            wanted.includes(String(option.id).toLowerCase()) ||
            wanted.includes(String(option.label ?? "").toLowerCase())
        );

        if (chosen.length === 0) {
          throw new Error(`Nothing on this page has ${what} ${asList(requested).join(" or ")}.`);
        }

        dropdown.values = chosen;
        applied.push(chosen.map((option) => String(option.label ?? option.id)).join(" and "));
      };

      // Both ends are needed before this page treats a range as set, so one end given becomes the far
      // side of what it could mean rather than a filter that silently does nothing.
      const setRange = (
        key: "dueDate" | "documentSubmissionDate",
        from: unknown,
        to: unknown,
        label: string
      ) => {
        if (from === undefined && to === undefined) {
          return;
        }

        this.filters.dateRange[key] = {
          startDate: from === undefined ? moment("1900-01-01") : asMoment(from),
          endDate: to === undefined ? moment().add(50, "years") : asMoment(to),
        } as never;
        applied.push(`${label} ${[from, to].filter(Boolean).join(" to ")}`);
      };

      if (args.patientStatus !== undefined) {
        setDropdown("Patient status", args.patientStatus, "patient status");
      }

      if (args.documentType !== undefined) {
        setDropdown("Document type", args.documentType, "document type");
      }

      if (args.documentStatus !== undefined) {
        setDropdown("Document status", args.documentStatus, "document status");
      }

      if (args.assignedNurse !== undefined) {
        setDropdown("Assigned nurse", args.assignedNurse, "assigned nurse");
      }

      if (args.search !== undefined) {
        this.filters.freeText = String(args.search) as never;
        applied.push(`"${String(args.search)}"`);
      }

      const hadRange =
        args.dueFrom !== undefined ||
        args.dueTo !== undefined ||
        args.submittedFrom !== undefined ||
        args.submittedTo !== undefined;

      setRange("dueDate", args.dueFrom, args.dueTo, "due");
      setRange("documentSubmissionDate", args.submittedFrom, args.submittedTo, "submitted");

      // A range is a server-side fetch; everything else re-filters what is already on screen.
      if (hadRange) {
        this.loadTable();
      } else {
        this.onFilterChange();
      }

      return applied.length === 0
        ? "Re-applied the patient document filters"
        : `Showing patient documents: ${applied.join(", ")}`;
    });

    this.$scope.$on("$destroy", releasePatientDocumentsFilter);

    this.dropdownFilterEvents = {
      onSelectionChanged: () => this.onFilterChange(),
    };

    this.dateRangeOptions = {
      ...dateRangeDefaultOptions,
      eventHandlers: {
        "apply.daterangepicker": () => this.loadTable(),
      },
    };

    this.filters = {
      freeText: "",
      dateRange: {
        dueDate: {
          startDate: null,
          endDate: null,
        },

        documentSubmissionDate: {
          startDate: null,
          endDate: null,
        },
      },
      dropdowns: [
        {
          title: "Patient status",
          entityPath: ["patient", "status"],
          options: [],
          values: [],
        },
        {
          title: "Document type",
          entityPath: ["document", "type", "title"],
          options: [],
          values: [],
        },
        {
          title: "Document status",
          entityPath: ["document", "status"],
          options: [],
          values: [],
        },
        {
          title: "Assigned nurse",
          entityPath: ["assignedNurse", "name"],
          options: [],
          values: [],
        },
      ],
    };

    this.dropdownFilterManager = this.createDropdownFilter<AgencyPatientDocument>({
      dropdowns: this.filters.dropdowns,
    });

    this.loadTable();
  }

  onFilterChange = () => {
    this.setTable(this.documents);
  };

  getDocumentStatusLabelColor = (status: PatientDocumentStatus): string => {
    switch (status) {
      case "OPEN_FOR_RESUBMISSION":
      case "IN_PROGRESS":
      case "CHANGE_REQUESTED":
        return "orange";

      case "SENT":
      case "COMPLETED":
      case "SIGNED":
        return "blue";

      case "APPROVED":
      case "SENT_TO_PHYSICIAN":
        return "green";

      case "MISSING":
        return "red";

      default:
        this.assertNever(status);
    }
  };

  openPatientModal = (patientId: PatientId) => {
    this.$rootScope.openPatientModal(patientId);
  };

  resetDueDate = () => {
    this.filters.dateRange.dueDate = {
      startDate: null,
      endDate: null,
    };

    this.loadTable();
  };

  resetDocumentSubmissionDate = () => {
    this.filters.dateRange.documentSubmissionDate = {
      startDate: null,
      endDate: null,
    };

    this.loadTable();
  };

  loadTable = () => {
    this.isLoading = true;

    const filters = {
      dueDate:
        this.filters.dateRange.dueDate.startDate !== null &&
        this.filters.dateRange.dueDate.endDate !== null
          ? {
              from: this.dateUtils.ymdStringToLocalDate(
                this.filters.dateRange.dueDate.startDate.format("YYYY-MM-DD")
              ),
              to: this.dateUtils.ymdStringToLocalDate(
                this.filters.dateRange.dueDate.endDate.format("YYYY-MM-DD")
              ),
            }
          : undefined,

      documentSubmissionDate:
        this.filters.dateRange.documentSubmissionDate.startDate !== null &&
        this.filters.dateRange.documentSubmissionDate.endDate !== null
          ? {
              from: this.dateUtils.ymdStringToLocalDate(
                this.filters.dateRange.documentSubmissionDate.startDate.format("YYYY-MM-DD")
              ),
              to: this.dateUtils.ymdStringToLocalDate(
                this.filters.dateRange.documentSubmissionDate.endDate.format("YYYY-MM-DD")
              ),
            }
          : undefined,
    };

    this.patientDocumentService
      .getPatientsDocuments(filters)
      .then((documents) => {
        const parsed = documents.map(this.patientDocumentService.parseAgencyPatientDocument);
        this.documents = parsed;

        for (const document of parsed) {
          const status = document.document.status;
          const statusStats = this.documentStatusesForStats[status];
          if (statusStats) {
            statusStats.count++;
          }
        }

        this.setTable(parsed);
      })
      .catch(() => {
        this.toaster.pop("error", "Something went wrong", "Could not get patients documents list");
      })
      .finally(() => (this.isLoading = false));
  };

  private setTable(documents: AgencyPatientDocument[]) {
    const filteredDocuments = this.filterDocuments(documents);
    this.table = new this.NgTableParams(tableSettings, { dataset: filteredDocuments });
    this.dropdownFilterManager.setOptionsFromLocalData(documents);
  }

  private filterDocuments(documents: AgencyPatientDocument[]) {
    const query = this.filters.freeText.toLowerCase();

    return documents.filter((document) => {
      return (
        this.dropdownFilterManager.filter(document) &&
        this.filterDocumentByFreeText(document, query)
      );
    });
  }

  private filterDocumentByFreeText(document: AgencyPatientDocument, lowerCaseQuery: string) {
    return (
      lowerCaseQuery === "" ||
      document.patient.name.toLowerCase().includes(lowerCaseQuery) ||
      document.patient.displayId?.toString() === lowerCaseQuery
    );
  }
}
